import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getStEvalNoteForm,
  updateStEvalNoteForm,
} from "../../../../../services/patientService";
import {
  BrainIcon,
  DeleteIcon,
  FileCheckIcon,
  HeartPulseIcon,
  HomeIcon,
  MedicalNotesIcon,
  PlusIcon,
  RunningIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  ST_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ATTENTION_OPTIONS,
  COMPENSATORY_STRATEGY_OPTIONS,
  GOAL_TYPE_OPTIONS,
  HEARING_VISION_OPTIONS,
  LIQUIDS_CONSISTENCY_OPTIONS,
  ORAL_PHASE_OPTIONS,
  PHARYNGEAL_PHASE_OPTIONS,
  PRIMARY_INTERVENTION_OPTIONS,
  RAILING_PRESENT_OPTIONS,
  RESIDENCE_TYPE_OPTIONS,
  SOLIDS_CONSISTENCY_OPTIONS,
  ST_EVAL_ARRAY_KEYS,
  emptyMeasurableGoalRow,
  emptyStEvalPayload,
  formatReferringDiagnosis,
  normalizeMeasurableGoals,
  toggleInList,
  today,
} from "./stEvalNoteContent";
import StNotesCommonHeader from "./StNotesCommonHeader";
import {
  normalizeStCommonHeaderState,
  resolveStNotesCommonHeader,
  withNormalizedStCommonHeader,
} from "./stNotesCommonHeaderUtils";
import {
  assertPtVisitMetaForFlag,
  buildPtVisitMetaProps,
  pickPtVisitMeta,
} from "../ptNotes/ptVisitCommon";

const EVAL_DATE_MAX = new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  ...emptyStEvalPayload(),
  evalDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const DateFieldInput = ({ name, value, canEdit, printMode, onChange }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const SwallowPanel = ({ title, children }) => (
  <div className="h-full rounded-md border border-violet-100 bg-violet-50/50 flex flex-col min-h-0">
    <div className="px-2.5 py-1.5 border-b border-violet-100">
      <p className="text-[10px] font-bold uppercase tracking-wide text-violet-900">
        {title}
      </p>
    </div>
    <div className="p-2.5 flex-1">{children}</div>
  </div>
);

SwallowPanel.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const StEvalNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyStEvalPayload();
      const loadedPayload =
        data.payload &&
        typeof data.payload === "object" &&
        !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickPtVisitMeta(loadedPayload, data),
        evalDate: data.evalDate || loadedPayload.evalDate || data.visitDate || today(),
        commonHeader: normalizeStCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        measurableGoals: normalizeMeasurableGoals(loadedPayload.measurableGoals),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      ST_EVAL_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : [];
      });

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveStNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Speech Therapy Evaluation Note",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            evalDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyStEvalPayload(),
              commonHeader,
              referringDiagnosis: formatReferringDiagnosis(patient),
            },
          });
          return;
        }

        const res = await getStEvalNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveStNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            commonHeader,
            referringDiagnosis:
              existingPayload.referringDiagnosis ||
              formatReferringDiagnosis(patient),
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateGoalRow = (id, patch) => {
    setValues((prev) => ({
      ...prev,
      measurableGoals: (prev.measurableGoals || []).map((row) =>
        row.id === id ? { ...row, ...patch } : row,
      ),
    }));
  };

  const addGoalRow = () => {
    setValues((prev) => ({
      ...prev,
      measurableGoals: [
        ...(prev.measurableGoals || []),
        emptyMeasurableGoalRow("STG", (prev.measurableGoals || []).length + 1),
      ],
    }));
  };

  const removeGoalRow = (id) => {
    setValues((prev) => {
      const next = (prev.measurableGoals || []).filter((row) => row.id !== id);
      return {
        ...prev,
        measurableGoals: next.length
          ? next
          : normalizeMeasurableGoals([], 1),
      };
    });
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        ST_FORM_EDITOR_ROLES,
        "View only — only ST can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const { caregiverSlot, filledByUserId, assertCaregiverForCreate } =
    useFormCaregiverPicker({
      patientId,
      formType: form?.formType || draft?.formType || clinicalForm?.formType,
      role,
      canEdit,
      preferredStaffId: draft?.visit?.staffId ?? null,
      currentFilledByUserId: form?.filledBy || clinicalForm?.filledBy || null,
    });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      evalDate,
      ...stFields
    } = values;

    const nestedPayload = withNormalizedStCommonHeader({
      ...stFields,
      onsetSurgeryDate: values.onsetSurgeryDate
        ? formatDateForInput(values.onsetSurgeryDate)
        : "",
      anticipatedDischargeDate: values.anticipatedDischargeDate
        ? formatDateForInput(values.anticipatedDischargeDate)
        : "",
      measurableGoals: (values.measurableGoals || []).map((row) => ({
        id: row.id,
        type: row.type === "LTG" ? "LTG" : "STG",
        description: row.description || "",
        target: row.target || "",
      })),
    });

    ST_EVAL_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      evalDate: evalDate ? formatDateForInput(evalDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    const visitMetaError = assertPtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateStEvalNoteForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.evalDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showResidenceOther = values.residenceType === "Other";
  const showInterventionOther = values.primaryIntervention === "Other";

  const diagnosisTags = (values.commonHeader?.diagnoses || []).filter(
    (d) => d?.diagnosisCode || d?.diagnosisName,
  );

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Speech Therapy Evaluation
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Initial Assessment &amp; Plan of Care
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Evaluation Date"
          dateValue={displayDateUS(values.evalDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400 leading-none">
                  Evaluation Date
                </div>
                <InputDatePicker
                  name="evalDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.evalDate) || ""}
                  onChange={(event) => setValue("evalDate", event.target.value)}
                  max={EVAL_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildPtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <StNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        {/* 7. Referral & Living Situation */}
        <Section icon={HomeIcon} title="7. Referral & Living Situation">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Referring Diagnosis / Reason for Referral" span="lg:col-span-2">
              <input
                type="text"
                className={fieldClass}
                value={values.referringDiagnosis || ""}
                disabled={!canEdit}
                placeholder="Enter diagnosis or reason for referral…"
                onChange={(e) => setValue("referringDiagnosis", e.target.value)}
              />
            </Field>
            <Field label="Onset / Surgery Date">
              <DateFieldInput
                name="onsetSurgeryDate"
                value={values.onsetSurgeryDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("onsetSurgeryDate", v)}
              />
            </Field>
            <Field label="Residence Type">
              {printMode || !canEdit ? (
                <div className={`${fieldClass} flex items-center`}>
                  {values.residenceType === "Other"
                    ? values.residenceTypeOther || "Other"
                    : values.residenceType || "—"}
                </div>
              ) : (
                <select
                  className={fieldClass}
                  value={values.residenceType || ""}
                  onChange={(e) => setValue("residenceType", e.target.value)}
                >
                  <option value="">Select…</option>
                  {RESIDENCE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            {showResidenceOther ? (
              <Field label="Residence Type (Other)">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.residenceTypeOther || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("residenceTypeOther", e.target.value)
                  }
                />
              </Field>
            ) : null}
            <Field label="Lives With">
              <input
                type="text"
                className={fieldClass}
                value={values.livesWith || ""}
                disabled={!canEdit}
                placeholder="e.g., Spouse, Alone"
                onChange={(e) => setValue("livesWith", e.target.value)}
              />
            </Field>
            <Field label="Entrance Access">
              <input
                type="text"
                className={fieldClass}
                value={values.entranceAccess || ""}
                disabled={!canEdit}
                placeholder="e.g., 3 steps with ramp"
                onChange={(e) => setValue("entranceAccess", e.target.value)}
              />
            </Field>
            <Field label="Railing Present">
              <ExclusiveChoiceGrid
                name="railingPresent"
                options={RAILING_PRESENT_OPTIONS}
                value={values.railingPresent || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("railingPresent", opt)}
                columns={2}
              />
            </Field>
          </div>
        </Section>

        {/* 8. Subjective History */}
        <Section icon={MedicalNotesIcon} title="8. Subjective History">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-3 gap-y-2 items-stretch">
            <Field label="Chief Complaint" span="flex flex-col h-full">
              <textarea
                rows={4}
                className={`${textareaClass} min-h-[5.5rem] h-full flex-1`}
                value={values.chiefComplaint || ""}
                disabled={!canEdit}
                placeholder="Patient's primary concern…"
                onChange={(e) => setValue("chiefComplaint", e.target.value)}
              />
            </Field>
            <Field label="History of Present Illness (HPI)" span="flex flex-col h-full">
              <textarea
                rows={4}
                className={`${textareaClass} min-h-[5.5rem] h-full flex-1`}
                value={values.historyPresentIllness || ""}
                disabled={!canEdit}
                placeholder="Onset, course, and relevant history…"
                onChange={(e) =>
                  setValue("historyPresentIllness", e.target.value)
                }
              />
            </Field>
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Prior Level of Function (Speech/Swallow)">
              <input
                type="text"
                className={fieldClass}
                value={values.priorLevelOfFunction || ""}
                disabled={!canEdit}
                placeholder="Baseline communication and swallow function…"
                onChange={(e) =>
                  setValue("priorLevelOfFunction", e.target.value)
                }
              />
            </Field>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Hearing / Vision Status
              </p>
              <CheckboxGrid
                options={HEARING_VISION_OPTIONS}
                values={values.hearingVisionStatus}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("hearingVisionStatus", opt)}
                columns={3}
              />
            </div>
          </div>
        </Section>

        {/* 9. Communication Assessment */}
        <Section icon={HeartPulseIcon} title="9. Communication Assessment">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-3 gap-y-2 items-stretch">
            <Field label="Receptive Language" span="flex flex-col h-full">
              <textarea
                rows={4}
                className={`${textareaClass} min-h-[5.5rem] h-full flex-1`}
                value={values.receptiveLanguage || ""}
                disabled={!canEdit}
                placeholder="Comprehension, following commands…"
                onChange={(e) => setValue("receptiveLanguage", e.target.value)}
              />
            </Field>
            <Field
              label="Speech Production (Articulation / Voice / Fluency)"
              span="flex flex-col h-full"
            >
              <textarea
                rows={4}
                className={`${textareaClass} min-h-[5.5rem] h-full flex-1`}
                value={values.speechProduction || ""}
                disabled={!canEdit}
                placeholder="Articulation, voice quality, fluency…"
                onChange={(e) => setValue("speechProduction", e.target.value)}
              />
            </Field>
            <Field label="Expressive Language" span="flex flex-col h-full">
              <textarea
                rows={4}
                className={`${textareaClass} min-h-[5.5rem] h-full flex-1`}
                value={values.expressiveLanguage || ""}
                disabled={!canEdit}
                placeholder="Verbal output, word finding, syntax…"
                onChange={(e) =>
                  setValue("expressiveLanguage", e.target.value)
                }
              />
            </Field>
            <Field label="Pragmatics" span="flex flex-col h-full">
              <textarea
                rows={4}
                className={`${textareaClass} min-h-[5.5rem] h-full flex-1`}
                value={values.pragmatics || ""}
                disabled={!canEdit}
                placeholder="Social communication, turn-taking…"
                onChange={(e) => setValue("pragmatics", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* 10. Swallowing (Dysphagia) */}
        <Section icon={RunningIcon} title="10. Swallowing (Dysphagia)">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">
            <SwallowPanel title="Oral Phase">
              <CheckboxGrid
                options={ORAL_PHASE_OPTIONS}
                values={values.oralPhase}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("oralPhase", opt)}
                columns={1}
              />
            </SwallowPanel>
            <SwallowPanel title="Pharyngeal Phase">
              <CheckboxGrid
                options={PHARYNGEAL_PHASE_OPTIONS}
                values={values.pharyngealPhase}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("pharyngealPhase", opt)}
                columns={1}
              />
            </SwallowPanel>
            <SwallowPanel title="Consistency Rec.">
              <div className="space-y-2">
                <Field label="Solids">
                  {printMode || !canEdit ? (
                    <div className={`${fieldClass} flex items-center`}>
                      {values.solidsConsistency || "—"}
                    </div>
                  ) : (
                    <select
                      className={fieldClass}
                      value={values.solidsConsistency || ""}
                      onChange={(e) =>
                        setValue("solidsConsistency", e.target.value)
                      }
                    >
                      <option value="">Select…</option>
                      {SOLIDS_CONSISTENCY_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                <Field label="Liquids">
                  {printMode || !canEdit ? (
                    <div className={`${fieldClass} flex items-center`}>
                      {values.liquidsConsistency || "—"}
                    </div>
                  ) : (
                    <select
                      className={fieldClass}
                      value={values.liquidsConsistency || ""}
                      onChange={(e) =>
                        setValue("liquidsConsistency", e.target.value)
                      }
                    >
                      <option value="">Select…</option>
                      {LIQUIDS_CONSISTENCY_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              </div>
            </SwallowPanel>
          </div>
          <div className="mt-2">
            <SubHeading>Compensatory Strategies Used</SubHeading>
            <CheckboxGrid
              options={COMPENSATORY_STRATEGY_OPTIONS}
              values={values.compensatoryStrategies}
              canEdit={canEdit}
              onToggle={(opt) =>
                toggleListValue("compensatoryStrategies", opt)
              }
              columns={3}
            />
          </div>
        </Section>

        {/* 11. Cognitive Assessment */}
        <Section icon={BrainIcon} title="11. Cognitive Assessment">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-2">
            <Field label="Orientation">
              <input
                type="text"
                className={fieldClass}
                value={values.orientation || ""}
                disabled={!canEdit}
                placeholder="e.g., Ox3 (Time, Place, Person)"
                onChange={(e) => setValue("orientation", e.target.value)}
              />
            </Field>
            <Field label="Attention">
              {printMode || !canEdit ? (
                <div className={`${fieldClass} flex items-center`}>
                  {values.attention || "—"}
                </div>
              ) : (
                <select
                  className={fieldClass}
                  value={values.attention || ""}
                  onChange={(e) => setValue("attention", e.target.value)}
                >
                  <option value="">Select…</option>
                  {ATTENTION_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Memory (Immediate / Short-Term)">
              <input
                type="text"
                className={fieldClass}
                value={values.memory || ""}
                disabled={!canEdit}
                placeholder="e.g., 2/3 recall with semantic cues"
                onChange={(e) => setValue("memory", e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-x-3 gap-y-2 items-stretch">
            <Field label="Problem Solving / Reasoning" span="flex flex-col h-full">
              <textarea
                rows={4}
                className={`${textareaClass} min-h-[5.5rem] h-full flex-1`}
                value={values.problemSolvingReasoning || ""}
                disabled={!canEdit}
                placeholder="Safety scenarios, judgment…"
                onChange={(e) =>
                  setValue("problemSolvingReasoning", e.target.value)
                }
              />
            </Field>
            <Field label="Executive Function" span="flex flex-col h-full">
              <textarea
                rows={4}
                className={`${textareaClass} min-h-[5.5rem] h-full flex-1`}
                value={values.executiveFunction || ""}
                disabled={!canEdit}
                placeholder="Multi-step tasks, planning, organization…"
                onChange={(e) =>
                  setValue("executiveFunction", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        {/* 12. Clinical Impression & Deficits */}
        <Section icon={FileCheckIcon} title="12. Clinical Impression & Deficits">
          <div className="rounded-md border border-rose-200 bg-rose-50/40 p-3 space-y-2">
            {diagnosisTags.length ? (
              <div className="flex flex-wrap gap-1.5">
                {diagnosisTags.map((row) => (
                  <span
                    key={row.id || `${row.diagnosisCode}-${row.diagnosisName}`}
                    className="inline-flex items-center rounded-md border border-rose-200 bg-rose-100/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-800"
                  >
                    ICD-10: {row.diagnosisCode || "—"}
                    {row.diagnosisName
                      ? ` (${String(row.diagnosisName).toUpperCase()})`
                      : ""}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-rose-700/80">
                Add ICD-10 codes in Section 5 (Diagnosis) to display here.
              </p>
            )}
            <textarea
              rows={6}
              className={`${textareaClass} min-h-[8rem] bg-white`}
              value={values.clinicalImpression || ""}
              disabled={!canEdit}
              placeholder="Summarize clinical findings, functional limitations, and skilled ST need…"
              onChange={(e) =>
                setValue("clinicalImpression", e.target.value)
              }
            />
          </div>
        </Section>

        {/* 13. Plan of Care */}
        <Section icon={TargetIcon} title="13. Plan of Care (POC)">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-2">
            <Field label="Visit Frequency">
              <input
                type="text"
                className={fieldClass}
                value={values.visitFrequency || ""}
                disabled={!canEdit}
                placeholder="e.g., 3x Week / 4 Weeks"
                onChange={(e) => setValue("visitFrequency", e.target.value)}
              />
            </Field>
            <Field label="Anticipated Discharge">
              <DateFieldInput
                name="anticipatedDischargeDate"
                value={values.anticipatedDischargeDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("anticipatedDischargeDate", v)}
              />
            </Field>
            <Field label="Primary Intervention">
              {printMode || !canEdit ? (
                <div className={`${fieldClass} flex items-center`}>
                  {values.primaryIntervention === "Other"
                    ? values.primaryInterventionOther || "Other"
                    : values.primaryIntervention || "—"}
                </div>
              ) : (
                <select
                  className={fieldClass}
                  value={values.primaryIntervention || ""}
                  onChange={(e) =>
                    setValue("primaryIntervention", e.target.value)
                  }
                >
                  <option value="">Select…</option>
                  {PRIMARY_INTERVENTION_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
          {showInterventionOther ? (
            <div className="mt-2 max-w-md">
              <Field label="Primary Intervention (Other)">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.primaryInterventionOther || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("primaryInterventionOther", e.target.value)
                  }
                />
              </Field>
            </div>
          ) : null}

          <div className="mt-3">
            <SubHeading>Measurable Goals</SubHeading>
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full table-fixed text-left text-[11px]">
                <thead>
                  <tr className="bg-slate-50 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-2.5 py-2 w-[4.5rem]">Type</th>
                    <th className="px-2.5 py-2">Goal Description</th>
                    <th className="px-2.5 py-2 w-[28%]">Target</th>
                    {canEdit ? (
                      <th className="px-2.5 py-2 w-10 text-right"> </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {(values.measurableGoals || []).map((row) => (
                    <tr key={row.id} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 align-middle">
                        {printMode || !canEdit ? (
                          <div className={`${fieldClass} flex items-center justify-center font-semibold`}>
                            {row.type || "STG"}
                          </div>
                        ) : (
                          <select
                            className={fieldClass}
                            value={row.type || "STG"}
                            onChange={(e) =>
                              updateGoalRow(row.id, { type: e.target.value })
                            }
                          >
                            {GOAL_TYPE_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-2 py-1.5 align-middle">
                        <textarea
                          rows={3}
                          className={`${textareaClass} h-[4.75rem] min-h-[4.75rem] resize-none`}
                          value={row.description || ""}
                          disabled={!canEdit}
                          placeholder="Enter measurable goal…"
                          onChange={(e) =>
                            updateGoalRow(row.id, {
                              description: e.target.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-1.5 align-middle">
                        <input
                          type="text"
                          className={fieldClass}
                          value={row.target || ""}
                          disabled={!canEdit}
                          placeholder="e.g., 80% accuracy"
                          onChange={(e) =>
                            updateGoalRow(row.id, { target: e.target.value })
                          }
                        />
                      </td>
                      {canEdit ? (
                        <td className="px-2 py-1.5 align-middle text-right">
                          <button
                            type="button"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
                            aria-label="Remove goal"
                            onClick={() => removeGoalRow(row.id)}
                          >
                            <DeleteIcon className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {canEdit ? (
              <button
                type="button"
                className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-700"
                onClick={addGoalRow}
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Add New Goal
              </button>
            ) : null}
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          canEdit={canEdit}
          canSign={canSign}
          printMode={printMode}
          values={values}
          onPatientSign={signatures.handlePatientSign}
          onClinicianSign={handleClinicianSign}
          onChange={setValues}
          clinicianLabel="Speech Therapist Signature"
        />

        {isReviewer ? (
          <OfficeReviewSection
            canEdit={canEdit}
            reviewComments={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        ) : values.reviewComments ? (
          <OfficeCommentsNote comments={values.reviewComments} />
        ) : null}

        {!printMode &&
          (permissions.canSendToOffice ||
            permissions.canToBeSign ||
            permissions.canReview ||
            permissions.canEdit) && (
            <FormActionBar
              permissions={permissions}
              status={form.status}
              canEdit={canEdit}
              saving={saving}
              onSubmit={submit}
            />
          )}
      </div>
    </div>
  );
};

StEvalNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default StEvalNoteForm;
